/* =============================================================================
   hd-pen.js —— 贝塞尔钢笔 + 节点编辑（2026-09-14 批次3）

   两件事：
     · 钢笔：点一下加一个锚点（直线段）；按下后拖拽可拉出贝塞尔手柄（曲线段）；
            双击 / 点「Finish」结束路径
     · 节点编辑：选中任意路径对象即显示锚点与手柄，拖锚点改位置、拖手柄改曲率、
            双击线段插节点、选中节点按 Delete 删除

   设计要点（沿用同族铁律）：
     · 节点**不建成 fabric 对象** —— 走 after:render 自绘 + 手动命中，
       故不污染图层列表 / 不入撤销栈 / 不被导出（与参考线、辅助线同一机制）
     · 事件必须在**捕获阶段**拦 `mousedown`/`touchstart`（fabric 只监听这两个），
       命中节点时才 stopPropagation —— 否则 fabric 会抢先取消选中
     · 线宽/半径一律按 `_pfmDisplayScale` 反算（§5），否则细到看不见
     · 事件绑定可补绑（§4）；selection 事件延后一拍（§4）
   ============================================================================= */
(function () {
  'use strict';

  var D = null, FROOT = null;
  var ready = false;

  var PEN = false;              // 钢笔模式
  var _draft = null;            // { nodes:[], obj }  正在画的路径
  var _penDown = false, _penMoved = false;
  var _edit = null;             // 正在编辑的路径对象
  var _nodes = [];              // 节点模型（**路径局部坐标**）
  var _closed = false;
  var _sel = -1;                // 选中节点索引
  var _drag = null;             // { kind:'node'|'hin'|'hout', i }
  var _mBound = false, _tBound = false, _sBound = false, _tabBound = false;
  var _origSelection = null, _origCursor = null;   // 进入钢笔前的原值（退出时恢复）
  var _lastDown = 0, _lastPos = null;           // 自行检测双击（不依赖 dblclick 事件）

  function q(id) { return document.getElementById(id); }

  /* 开发期调试钩子：页面侧设 window.__PEN_DBG = [] 后才记录，否则零开销 */
  function dbg(ev, data) {
    try {
      if (!window.__PEN_DBG) return;
      window.__PEN_DBG.push({ ev: ev, t: Date.now(), d: data });
      if (window.__PEN_DBG.length > 200) window.__PEN_DBG.shift();
    } catch (e) { }
  }

  function CV() {
    try { if (D && D.canvas) return D.canvas() || null; } catch (e) { }
    try { return window.__pfmCanvas || null; } catch (e) { }
    return null;
  }

  function toast(m) {
    try {
      if (D && D.toast) { D.toast(m); return; }
      if (window.__pfmToast) window.__pfmToast(m);
    } catch (e) { }
  }

  function dispScale() {
    var c = CV();
    var v = c && c._pfmDisplayScale;
    return (typeof v === 'number' && v > 0.001) ? v : 0.27;
  }

  function cssToCanvas(clientX, clientY) {
    var c = CV(); if (!c) return null;
    var el = c.upperCanvasEl; if (!el) return null;
    var r = el.getBoundingClientRect();
    if (r.width <= 0 || r.height <= 0) return null;
    var x = (clientX - r.left) * (el.width / r.width);
    var y = (clientY - r.top) * (el.height / r.height);
    var inv = fabric.util.invertTransform(c.viewportTransform || [1, 0, 0, 1, 0, 0]);
    return fabric.util.transformPoint({ x: x, y: y }, inv);
  }

  /* 不变式：left ≡ pathOffset 且无 scale/angle/flip
     ⇒ 世界坐标 = (路径坐标 − pathOffset) + left = 路径坐标
     ∴ 节点坐标直接就是画布坐标，**只需 viewportTransform**。
       （在此乘上对象矩阵会双重偏移 —— 实测落到 3440，画布外） */

  function clientToLocal(clientX, clientY) {
    return cssToCanvas(clientX, clientY);
  }

  /* ---------------------------------------------------------------------------
     路径数组 ↔ 节点模型
     节点：{ x, y, in:{x,y}|null, out:{x,y}|null }（局部坐标）
     Q（二次）统一转成等价的 C（三次），便于统一编辑：
       C1 = P0 + 2/3(P1-P0)   C2 = P2 + 2/3(P1-P2)
     --------------------------------------------------------------------------- */
  function pathToNodes(arr) {
    var ns = [], cur = null, closed = false;
    for (var i = 0; i < arr.length; i++) {
      var s = arr[i], cmd = String(s[0]).toUpperCase();
      if (cmd === 'M' || cmd === 'L') {
        cur = { x: s[1], y: s[2], in: null, out: null };
        ns.push(cur);
      } else if (cmd === 'C') {
        if (cur) cur.out = { x: s[1], y: s[2] };
        cur = { x: s[5], y: s[6], in: { x: s[3], y: s[4] }, out: null };
        ns.push(cur);
      } else if (cmd === 'Q') {
        var px = cur ? cur.x : 0, py = cur ? cur.y : 0;
        if (cur) cur.out = { x: px + 2 / 3 * (s[1] - px), y: py + 2 / 3 * (s[2] - py) };
        cur = {
          x: s[3], y: s[4],
          in: { x: s[3] + 2 / 3 * (s[1] - s[3]), y: s[4] + 2 / 3 * (s[2] - s[4]) },
          out: null
        };
        ns.push(cur);
      } else if (cmd === 'Z') {
        closed = true;
      }
    }
    return { nodes: ns, closed: closed };
  }

  function nodesToPath(ns, closed) {
    if (!ns || ns.length === 0) return [];
    var arr = [['M', ns[0].x, ns[0].y]];
    function seg(a, b) {
      var o = a.out, n = b.in, dx = b.x - a.x, dy = b.y - a.y;
      if (o || n) {
        var c1 = o || { x: a.x + dx / 3, y: a.y + dy / 3 };
        var c2 = n || { x: a.x + dx * 2 / 3, y: a.y + dy * 2 / 3 };
        return ['C', c1.x, c1.y, c2.x, c2.y, b.x, b.y];
      }
      return ['L', b.x, b.y];
    }
    for (var i = 1; i < ns.length; i++) arr.push(seg(ns[i - 1], ns[i]));
    if (closed && ns.length > 1) {
      arr.push(seg(ns[ns.length - 1], ns[0]));
      arr.push(['Z']);
    }
    return arr;
  }

  /* ---------------------------------------------------------------------------
     编辑态
     --------------------------------------------------------------------------- */
  function beginEdit(obj) {
    if (!obj || obj === _edit) return;
    endEdit(true);
    var arr = obj.path;
    if (!arr || !arr.length) return;
    _edit = obj;
    try {
      var m = pathToNodes(arr);
      var off = obj.pathOffset || { x: 0, y: 0 };
      var mat = obj.calcTransformMatrix();
      /* 只有当对象**确实被整体变换过**才需焙焙；否则节点已是世界坐标，
         无谓地调 _setPath 反而会扰动 pathOffset。 */
      var needsBake = (Math.abs(obj.scaleX || 1) !== 1) || (Math.abs(obj.scaleY || 1) !== 1) ||
        (obj.angle || 0) !== 0 || obj.flipX || obj.flipY;
      /* 把「对象当前世界位置」焙焙进节点：
         路径坐标 → 局部坐标(减 pathOffset) → 世界坐标 */
      if (needsBake) {
        m.nodes.forEach(function (n) {
          var w = fabric.util.transformPoint({ x: n.x - off.x, y: n.y - off.y }, mat);
          n.x = w.x; n.y = w.y;
          if (n.in) { var a = fabric.util.transformPoint({ x: n.in.x - off.x, y: n.in.y - off.y }, mat); n.in = { x: a.x, y: a.y }; }
          if (n.out) { var b = fabric.util.transformPoint({ x: n.out.x - off.x, y: n.out.y - off.y }, mat); n.out = { x: b.x, y: b.y }; }
        });
        obj.set({ scaleX: 1, scaleY: 1, angle: 0, flipX: false, flipY: false });
      }
      _nodes = m.nodes;
      _closed = m.closed;
      if (needsBake && obj._setPath) obj._setPath(nodesToPath(_nodes, _closed));
      syncAnchor(obj);
    } catch (e) { }
    _sel = -1;
    var c = CV();
    try { if (c) c.requestRenderAll(); } catch (e) { }
  }

  function endEdit(silent) {
    if (!_edit) return;
    _edit = null; _nodes = []; _closed = false; _sel = -1; _drag = null;
    var c = CV();
    if (!silent) { try { if (c) c.requestRenderAll(); } catch (e) { } }
  }

  function applyNodes() {
    if (!_edit) return;
    try {
      var arr = nodesToPath(_nodes, _closed);
      /* 必须走 _setPath（会重算 pathOffset 与包围盒）；直接 set({path}) 不会 */
      if (_edit._setPath) _edit._setPath(arr);
      else _edit.set({ path: arr });
      _edit.set({ dirty: true });
      syncAnchor(_edit);        /* _setPath 后 pathOffset 变了 → 必须同步 left/top */
    } catch (e) { }
    var c = CV();
    try { if (c) c.requestRenderAll(); } catch (e) { }
  }

  /* ---------------------------------------------------------------------------
     绘制节点（after:render 自绘）
     --------------------------------------------------------------------------- */
  function drawNodes() {
    var c = CV(); if (!c) return;
    var nodeSet = (PEN && _draft) ? _draft.nodes : _nodes;
    var host = (PEN && _draft) ? _draft.obj : _edit;
    if (!host || !nodeSet || !nodeSet.length) return;
    try {
      var ctx = c.getContext(); if (!ctx) return;
      var sc = dispScale();
      var vpt = c.viewportTransform || [1, 0, 0, 1, 0, 0];
      var m = vpt;   /* 节点坐标 ≡ 画布坐标，不叠加对象矩阵 */
      var R = Math.max(3, Math.round(5 / sc));
      var Rh = Math.max(2, Math.round(3.5 / sc));
      var lw = Math.max(1, Math.round(1.2 / sc));

      ctx.save();
      ctx.globalAlpha = 1;
      ctx.globalCompositeOperation = 'source-over';
      ctx.strokeStyle = '#0EA5E9';
      ctx.lineWidth = lw;

      /* 手柄连线 + 手柄点 */
      for (var i = 0; i < nodeSet.length; i++) {
        var n = nodeSet[i];
        var ap = fabric.util.transformPoint({ x: n.x, y: n.y }, m);
        [n.in, n.out].forEach(function (h) {
          if (!h) return;
          var hp = fabric.util.transformPoint({ x: h.x, y: h.y }, m);
          ctx.beginPath();
          ctx.moveTo(ap.x, ap.y);
          ctx.lineTo(hp.x, hp.y);
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(hp.x, hp.y, Rh, 0, Math.PI * 2);
          ctx.fillStyle = '#FFFFFF';
          ctx.fill();
          ctx.stroke();
        });
      }

      /* 锚点 */
      for (var k = 0; k < nodeSet.length; k++) {
        var p2 = fabric.util.transformPoint({ x: nodeSet[k].x, y: nodeSet[k].y }, m);
        ctx.beginPath();
        ctx.arc(p2.x, p2.y, R, 0, Math.PI * 2);
        ctx.fillStyle = (k === _sel) ? '#EC4899' : '#FFFFFF';
        ctx.fill();
        ctx.stroke();
      }
      ctx.restore();
    } catch (e) { }
  }

  /* ---------------------------------------------------------------------------
     命中
     --------------------------------------------------------------------------- */
  function hitNode(clientX, clientY) {
    var host = PEN && _draft ? _draft.obj : _edit;
    var nodeSet = PEN && _draft ? _draft.nodes : _nodes;
    if (!host || !nodeSet || !nodeSet.length) return null;

    var c = CV(), el = c && c.upperCanvasEl;
    if (!el) return null;
    var r = el.getBoundingClientRect();
    var elW = el.width, elH = el.height;
    var vpt = c.viewportTransform || [1, 0, 0, 1, 0, 0];
    /* 节点坐标 ≡ 画布坐标 → 只用 viewportTransform（见上不变式） */
    function toClient(p) {
      var t = fabric.util.transformPoint(p, vpt);
      return { x: t.x * (r.width / elW) + r.left, y: t.y * (r.height / elH) + r.top };
    }
    var TOL = 10;   // 屏幕像素

    /* 手柄优先（画在上层），其次锚点 */
    for (var i = 0; i < nodeSet.length; i++) {
      var n = nodeSet[i];
      if (n.out) { var a = toClient(n.out); if (Math.abs(a.x - clientX) <= TOL && Math.abs(a.y - clientY) <= TOL) return { kind: 'hout', i: i }; }
      if (n.in) { var b = toClient(n.in); if (Math.abs(b.x - clientX) <= TOL && Math.abs(b.y - clientY) <= TOL) return { kind: 'hin', i: i }; }
    }
    for (var j = 0; j < nodeSet.length; j++) {
      var p = toClient({ x: nodeSet[j].x, y: nodeSet[j].y });
      if (Math.abs(p.x - clientX) <= TOL && Math.abs(p.y - clientY) <= TOL) return { kind: 'node', i: j };
    }
    return null;
  }

  /* ---------------------------------------------------------------------------
     指针交互（捕获阶段拦 mousedown / touchstart）
     --------------------------------------------------------------------------- */
  function pointFrom(e) {
    if (e.touches && e.touches.length) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    if (e.changedTouches && e.changedTouches.length) return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
    return { x: e.clientX, y: e.clientY };
  }

  function onDown(e) {
    var pt = pointFrom(e);
    var now = Date.now();
    /* 自行检测双击：在 mousedown 上调 preventDefault() 会抑制浏览器的 dblclick 派发，
       故不能依赖 dblclick 事件。按「间隔 <320ms 且位置相近」判定。 */
    var isDbl = (now - _lastDown < 320) && _lastPos &&
      Math.abs(pt.x - _lastPos.x) < 14 && Math.abs(pt.y - _lastPos.y) < 14;
    dbg('down', { dbl: !!isDbl, dt: now - _lastDown, x: Math.round(pt.x), y: Math.round(pt.y), pen: PEN, edit: !!_edit });
    _lastDown = now; _lastPos = pt;

    /* ---- 钢笔模式：加点 / 拉手柄 ---- */
    if (PEN) {
      if (isDbl && _draft) {
        e.stopPropagation(); e.preventDefault();
        finishPen();
        return;
      }
      var cp = cssToCanvas(pt.x, pt.y);
      if (!cp) return;
      e.stopPropagation(); e.preventDefault();
      _penDown = true; _penMoved = false;
      if (!_draft) {
        _draft = {
          nodes: [{ x: cp.x, y: cp.y, in: null, out: null }],
          obj: buildObj([{ x: cp.x, y: cp.y, in: null, out: null }], false)
        };
        try { if (D && D.add) D.add(_draft.obj, { noSelect: true }); } catch (er) { }
      } else {
        _draft.nodes.push({ x: cp.x, y: cp.y, in: null, out: null });
        refreshDraft();
      }
      var c0 = CV();
      try { if (c0) c0.requestRenderAll(); } catch (er) { }
      return;
    }

    /* ---- 双击路径线段 → 插入节点 ---- */
    if (isDbl && _edit) {
      if (tryInsertNode(pt)) {
        e.stopPropagation(); e.preventDefault();
        return;
      }
    }

    /* ---- 节点编辑：命中则接管 ---- */
    var hit = hitNode(pt.x, pt.y);
    if (hit) {
      e.stopPropagation(); e.preventDefault();
      _drag = hit;
      _sel = hit.i;
      render();
      var c = CV();
      try { if (c) c.requestRenderAll(); } catch (er) { }
    }
  }

  function onMove(e) {
    if (!_drag && !_penDown) return;
    var pt = pointFrom(e);

    if (PEN && _penDown && _draft) {
      var cp = cssToCanvas(pt.x, pt.y);
      if (!cp) return;
      _penMoved = true;
      /* 拖拽 → 给「上一个节点」拉出 out 手柄（镜像到当前点的 in） */
      var n = _draft.nodes[_draft.nodes.length - 1];
      n.out = { x: cp.x, y: cp.y };
      n.in = { x: 2 * n.x - cp.x, y: 2 * n.y - cp.y };
      refreshDraft();
      e.stopPropagation(); e.preventDefault();
      return;
    }

    if (_drag) {
      var lp = clientToLocal(pt.x, pt.y);
      if (!lp) return;
      var nd = _nodes[_drag.i];
      if (!nd) return;
      if (_drag.kind === 'node') {
        var dx = lp.x - nd.x, dy = lp.y - nd.y;
        nd.x = lp.x; nd.y = lp.y;
        if (nd.in) { nd.in.x += dx; nd.in.y += dy; }
        if (nd.out) { nd.out.x += dx; nd.out.y += dy; }
      } else if (_drag.kind === 'hout') {
        nd.out = { x: lp.x, y: lp.y };
      } else if (_drag.kind === 'hin') {
        nd.in = { x: lp.x, y: lp.y };
      }
      applyNodes();
      e.stopPropagation(); e.preventDefault();
    }
  }

  function onUp(e) {
    if (_penDown) {
      _penDown = false;
      if (_draft) refreshDraft();
      return;
    }
    if (_drag) {
      _drag = null;
      try { if (D && D.save) D.save(); } catch (er) { }
      var c = CV();
      try { if (c) c.requestRenderAll(); } catch (er) { }
    }
  }

  /* 在最近的线段中点插入节点；成功返回 true */
  function tryInsertNode(pt) {
    if (!_edit) { dbg('insert', { r: 'no-edit' }); return false; }
    var lp = clientToLocal(pt.x, pt.y);
    if (!lp) { dbg('insert', { r: 'no-local' }); return false; }
    var best = -1, bestD = Infinity;
    for (var i = 1; i < _nodes.length; i++) {
      var a = _nodes[i - 1], b = _nodes[i];
      var mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
      var d = (mx - lp.x) * (mx - lp.x) + (my - lp.y) * (my - lp.y);
      if (d < bestD) { bestD = d; best = i; }
    }
    if (best < 0) { dbg('insert', { r: 'no-seg' }); return false; }
    var sc = dispScale() * Math.abs(_edit.scaleX || 1) * Math.abs(_edit.scaleY || 1);
    var TOL = Math.pow(90 / Math.sqrt(Math.max(0.0001, sc * sc)), 2);
    dbg('insert', { r: 'calc', best: best, bestD: Math.round(bestD), TOL: Math.round(TOL) });
    if (bestD > TOL) return false;
    _nodes.splice(best, 0, { x: lp.x, y: lp.y, in: null, out: null });
    _sel = best;
    applyNodes();
    try { if (D && D.save) D.save(); } catch (er) { }
    render();
    return true;
  }

  function onKey(e) {
    var t = e.target;
    if (t && t.tagName && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
    if (e.key === 'Escape') {
      if (PEN && _draft) { finishPen(); return; }
      if (PEN) { setPen(false); return; }
    }
    if (e.key === 'Enter' && PEN && _draft) { finishPen(); return; }
    if ((e.key === 'Delete' || e.key === 'Backspace') && _edit && _sel >= 0 && !PEN) {
      e.preventDefault();
      _nodes.splice(_sel, 1);
      _sel = -1;
      applyNodes();
      try { if (D && D.save) D.save(); } catch (er) { }
      render();
    }
  }

  /* ---------------------------------------------------------------------------
     钢笔
     --------------------------------------------------------------------------- */
  /* 路径坐标用的是「画布坐标」，而 fabric 把 Path 的局部图像**居中于原点**
     （_render 里画的是 `point − pathOffset`，范围 [−w/2, +w/2]）。
     ∴ 必须用 originX/Y = 'center' 且 left/top = pathOffset；
     若用 'left' 会把「中心」当成「左边缘」→ 整体偏移半个宽度
     （实测：差 319≈width/2=325 —— 这就是截图里路径与节点分离的原因）。 */
  function syncAnchor(o) {
    if (!o) return;
    try {
      var off = o.pathOffset || { x: 0, y: 0 };
      o.set({ left: off.x, top: off.y });
      if (o.setCoords) o.setCoords();
    } catch (e) { }
  }

  function buildObj(nodes, closed) {
    var arr = nodesToPath(nodes, closed);
    var o = new fabric.Path(arr, {
      left: 0, top: 0, originX: 'center', originY: 'center',
      fill: '',
      stroke: '#7C3AED',
      strokeWidth: Math.max(2, Math.round(3 / dispScale())),
      strokeLineCap: 'round',
      strokeLineJoin: 'round',
      strokeUniform: true
    });
    o.pfmName = 'Path';
    o.pfmShapeKind = 'path';
    syncAnchor(o);
    return o;
  }

  function refreshDraft() {
    if (!_draft || !_draft.obj) return;
    var arr = nodesToPath(_draft.nodes, false);
    try {
      if (_draft.obj._setPath) _draft.obj._setPath(arr);
      else _draft.obj.set({ path: arr });
      _draft.obj.set({ dirty: true });
      syncAnchor(_draft.obj);
    } catch (e) { }
    var c = CV();
    try { if (c) c.requestRenderAll(); } catch (e) { }
  }

  function finishPen() {
    if (!_draft) { setPen(false); return; }
    var d = _draft;
    _draft = null;
    if (d.nodes.length < 2) {
      /* 只有一个点 → 撤销该对象 */
      var c0 = CV();
      try { if (c0 && d.obj) c0.remove(d.obj); } catch (e) { }
      try { if (c0) c0.requestRenderAll(); } catch (e) { }
      toast('Need at least 2 points');
      return;
    }
    /* 清掉双击产生的重合点（双击会先派发 down/up 两次，各加一个重合点） */
    var dupTol = 12 / dispScale();
    while (d.nodes.length > 2) {
      var la = d.nodes[d.nodes.length - 1], lb = d.nodes[d.nodes.length - 2];
      if (Math.hypot(la.x - lb.x, la.y - lb.y) <= dupTol) d.nodes.pop();
      else break;
    }
    /* 闭合：首尾点重合时自动闭合并转为可填充 */
    var a = d.nodes[0], b = d.nodes[d.nodes.length - 1];
    var dd = Math.hypot(a.x - b.x, a.y - b.y);
    var closeTol = 30 / dispScale();
    var closed = dd <= closeTol;
    if (closed) d.nodes.pop();
    var arr = nodesToPath(d.nodes, closed);
    try {
      if (d.obj._setPath) d.obj._setPath(arr);
      d.obj.set({ dirty: true });
      if (closed) d.obj.set({ fill: '#7C3AED' });
      syncAnchor(d.obj);
    } catch (e) { }
    try { if (D && D.save) D.save(); } catch (e) { }
    try { if (window.__pfmUpdateLayers) window.__pfmUpdateLayers(); } catch (e) { }
    var c = CV();
    try { if (c) { c.setActiveObject(d.obj); c.requestRenderAll(); } } catch (e) { }
    toast(closed ? 'Closed path created' : 'Path created');
    setPen(false);
  }

  function setPen(on) {
    if (PEN === on) return;
    if (!on && _draft) {
      /* 中途退出：把未完成的草稿对象移除（只有一个点时不残留） */
      var cd = CV();
      try { if (cd && _draft.obj) cd.remove(_draft.obj); } catch (e) { }
      _draft = null;
    }
    PEN = !!on;
    var c = CV();
    if (c) {
      try {
        if (PEN) {
          /* 记录原值：手机端的 selection 本就是 false，不可被覆盖后不还原 */
          if (_origSelection === null) _origSelection = c.selection;
          if (_origCursor === null) _origCursor = c.defaultCursor;
          c.selection = false;
          c.defaultCursor = 'crosshair';
        } else {
          if (_origSelection !== null) { c.selection = _origSelection; _origSelection = null; }
          if (_origCursor !== null) { c.defaultCursor = _origCursor; _origCursor = null; }
        }
      } catch (e) { }
      try { c.requestRenderAll(); } catch (e) { }
    }
    var el = q('pfmCanvasInner');
    if (el) el.classList.toggle('pfm-pen-mode', PEN);
    render();
  }

  /* ---------------------------------------------------------------------------
     参数化形状 → 路径（只有真路径才有节点；形状须先转曲线）
     复用批次4d 的 __hdVector.pathDataOf（内部已把 transform 烘焙进路径点）
     --------------------------------------------------------------------------- */
  function canConvert(o) {
    if (!o || o.type === 'activeSelection' || o.type === 'path') return false;
    return /^(rect|ellipse|circle|triangle|polygon|line)$/.test(String(o.type));
  }

  function convertToPath() {
    var c = CV(); if (!c) return;
    var o = null;
    try { o = c.getActiveObject(); } catch (e) { }
    if (!canConvert(o)) { toast('Select a shape first'); return; }
    var V = window.__hdVector;
    if (!V || !V.pathDataOf) { toast('Shape engine not ready'); return; }
    toast('Converting…');
    V.pathDataOf(o).then(function (d) {
      try {
        if (!d) { toast('Cannot convert this shape'); return; }
        var np = new fabric.Path(d, {
          left: 0, top: 0, originX: 'center', originY: 'center',
          fill: (o.fill === undefined ? '' : o.fill),
          stroke: (o.stroke === undefined ? '' : o.stroke),
          strokeWidth: o.strokeWidth || 0,
          opacity: (o.opacity === undefined ? 1 : o.opacity)
        });
        np.pfmName = o.pfmName || 'Path';
        np.pfmShapeKind = 'path';
        syncAnchor(np);        /* pathDataOf 返回画布坐标 → 同样需校正 */
        c.remove(o);
        c.add(np);
        c.setActiveObject(np);
        c.renderAll();
        try { if (D && D.save) D.save(); } catch (e) { }
        try { if (window.__pfmUpdateLayers) window.__pfmUpdateLayers(); } catch (e) { }
        beginEdit(np);
        render();
        toast('Converted to curve');
      } catch (e) {
        toast('Convert failed');
      }
    }).catch(function () { toast('Convert failed'); });
  }

  /* ---------------------------------------------------------------------------
     面板
     --------------------------------------------------------------------------- */
  function render() {
    if (!FROOT) return;
    var editing = !!_edit;
    var c = CV();
    var cur = null;
    try { cur = c && c.getActiveObject(); } catch (e) { }
    if (cur && cur.type === 'activeSelection') cur = null;
    var convertible = !PEN && canConvert(cur);

    var h = '';
    h += '<div class="pfm-pen-hint">'
      + (PEN ? 'Tap to add points, drag to curve.<br>Double-tap or Finish to end.'
        : (editing ? 'Drag the round handles to edit the path.<br>Double-tap a segment to add a point.'
          : (convertible ? 'This shape has no points yet.<br>Convert it to a curve to edit points.'
            : 'Start drawing, or select a path to edit its points.')))
      + '</div>';
    if (PEN) {
      h += '<button class="pfm-pen-btn" type="button" id="pfmPenFinish">Finish path</button>';
    } else {
      h += '<button class="pfm-pen-btn" type="button" id="pfmPenStart">Start drawing</button>';
    }
    if (convertible) {
      h += '<button class="pfm-pen-btn pfm-pen-btn2" type="button" id="pfmPenConvert">Convert to curve</button>';
    }
    if (editing) {
      h += '<div class="pfm-pen-meta">' + _nodes.length + ' point' + (_nodes.length === 1 ? '' : 's')
        + (_closed ? ' · closed' : '')
        + (_sel >= 0 ? ' · selected #' + (_sel + 1) : '') + '</div>';
    }
    FROOT.innerHTML = h;
    var b1 = q('pfmPenStart'); if (b1) b1.addEventListener('click', function () { setPen(true); });
    var b2 = q('pfmPenFinish'); if (b2) b2.addEventListener('click', finishPen);
    var b3 = q('pfmPenConvert'); if (b3) b3.addEventListener('click', convertToPath);
  }

  function syncPen() {
    if (!FROOT) return;
    var c = CV();
    var o = null;
    try { o = c && c.getActiveObject(); } catch (e) { }
    if (o && o.type === 'activeSelection') o = null;
    if (o && o.path && o.type === 'path' && !PEN) beginEdit(o);
    else if ((!o || o.type !== 'path') && !PEN) endEdit();
    render();
  }

  function syncSoon() {
    try { setTimeout(function () { try { syncPen(); } catch (e) { } }, 0); } catch (e) { }
  }

  /* ---------------------------------------------------------------------------
     绑定（可补绑）
     --------------------------------------------------------------------------- */
  function bindCanvas() {
    if (_mBound) return;
    var c = CV(); if (!c || !c.upperCanvasEl) return;
    _mBound = true;
    var el = c.upperCanvasEl;
    /* ⚠️ 捕获阶段：fabric 只监听 mousedown / touchstart，命中节点时必须截断 */
    el.addEventListener('mousedown', onDown, true);
    el.addEventListener('touchstart', onDown, { capture: true, passive: false });
    document.addEventListener('mousemove', onMove, true);
    document.addEventListener('touchmove', onMove, { capture: true, passive: false });
    document.addEventListener('mouseup', onUp, true);
    document.addEventListener('touchend', onUp, true);
    document.addEventListener('touchcancel', onUp, true);
    if (!_tBound) {
      _tBound = true;
      try { document.addEventListener('keydown', onKey); } catch (e) { }
    }
    if (!_sBound && c.on) {
      _sBound = true;
      try {
        c.on('selection:created', syncSoon);
        c.on('selection:updated', syncSoon);
        c.on('selection:cleared', syncSoon);
        c.on('after:render', drawNodes);
      } catch (e) { _sBound = false; }
    }
    bindToolTab();
  }

  function bindToolTab() {
    if (_tabBound) return;
    _tabBound = true;
    /* ⚠️ 2026-09-15: pen tab 已从左侧工具条移出（入口改到画板弹窗 → __pfmOpenPanel('pen')）。
       原写法 `var btn=…; if(!btn) return;` 会在 tab 不存在时**整段提前返回**，
       连下面「切到别的工具则退出钢笔」也一起失效 → 必须拆成「按需绑启动 / 始终绑退出」。 */
    var btn = document.querySelector('.pfm-stab[data-panel="pen"]');
    if (btn) btn.addEventListener('click', function () { setPen(true); });
    /* 切到别的工具 → 退出钢笔 */
    document.querySelectorAll('.pfm-stab').forEach(function (b) {
      if (b.dataset.panel === 'pen') return;
      b.addEventListener('click', function () { setPen(false); });
    });
  }

  function init(deps) {
    if (ready) { bindCanvas(); try { render(); } catch (e) { } return; }
    ready = true;
    D = deps || {};
    FROOT = q('pfmPenPanel');
    render();
    bindCanvas();
  }

  window.__hdPen = {
    init: init,
    setPen: setPen,
    finishPen: finishPen,
    isPen: function () { return PEN; },
    pathToNodes: pathToNodes,
    nodesToPath: nodesToPath,
    editTarget: function () { return _edit; },
    nodes: function () { return _nodes.slice(); },
    draft: function () { return _draft ? { n: _draft.nodes.length } : null; },
    beginEdit: beginEdit,
    endEdit: endEdit,
    applyNodes: applyNodes,
    hitNode: hitNode,
    syncPen: syncPen,
    convertToPath: convertToPath,
    tryInsertNode: tryInsertNode,
    syncAnchor: syncAnchor,
    canConvert: canConvert
  };

  try { if (window.__pfmPenBoot) window.__pfmPenBoot(); } catch (e) { }
})();
